import axios from 'axios';

// Dynamically determine API URL at RUNTIME (not build time)
// This ensures production uses production backend, not preview backend
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  
  // Local development - use localhost backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  
  // Preview environment (Emergent preview) - use preview backend URL
  if (hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  
  // Production (haden.in or any other domain) - use relative URLs
  // This ensures API calls go to the same domain's backend via /api/* routes
  return '';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================
// SESSION MANAGEMENT (Industry Standard - GA4 style)
// ============================================

// Generate or retrieve session ID from sessionStorage
// sessionStorage automatically clears when browser tab/window is closed
// This ensures a new session is created when user reopens the browser
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('haden_session_id');
  let sessionStart = sessionStorage.getItem('haden_session_start');
  
  if (!sessionId) {
    // Generate new UUID for session
    sessionId = crypto.randomUUID ? crypto.randomUUID() : 
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    sessionStart = new Date().toISOString();
    sessionStorage.setItem('haden_session_id', sessionId);
    sessionStorage.setItem('haden_session_start', sessionStart);
  }
  
  return sessionId;
};

// Get session start time
export const getSessionStart = () => {
  return sessionStorage.getItem('haden_session_start') || new Date().toISOString();
};

// End session (called on page unload)
export const endSession = () => {
  const sessionId = sessionStorage.getItem('haden_session_id');
  if (sessionId) {
    // Use sendBeacon for reliable delivery during page unload
    const data = JSON.stringify({
      session_id: sessionId,
      event_type: 'session_end',
      timestamp: new Date().toISOString()
    });
    
    const url = `${API_BASE_URL}/api/events/session-end`;
    
    // sendBeacon is more reliable than fetch/XHR during page unload
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, data);
    } else {
      // Fallback for older browsers
      fetch(url, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true
      }).catch(() => {});
    }
  }
};

// ============================================
// PUBLIC API
// ============================================

export const getContent = () => api.get('/api/content');

export const submitInquiry = (formData) => {
  return api.post('/api/inquiry', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const trackEvent = (eventType, eventData = {}) => {
  // Include screen info with events for more complete tracking
  const screenInfo = getScreenInfo();
  return api.post('/api/events/track', {
    event_type: eventType,
    event_data: {
      ...eventData,
      ...screenInfo
    },
    session_id: getSessionId(),
    session_start: getSessionStart()
  });
};

// Heatmap tracking
export const trackHeatmapEvent = (eventType, sectionId, data = {}) => {
  return api.post('/api/events/heatmap', {
    event_type: eventType,
    section_id: sectionId,
    session_id: getSessionId(),
    session_start: getSessionStart(),
    ...data
  });
};

// Get screen dimensions
export const getScreenInfo = () => {
  return {
    screen_width: window.screen?.width || window.innerWidth,
    screen_height: window.screen?.height || window.innerHeight,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio || 1
  };
};

// Visitor heartbeat (for real-time tracking)
export const sendVisitorHeartbeat = (page = '/') => {
  return api.post('/api/visitors/heartbeat', { 
    page,
    session_id: getSessionId(),
    session_start: getSessionStart(),
    ...getScreenInfo()
  });
};

// Auth API
export const login = (email, password) => {
  return api.post('/api/auth/login', { email, password });
};

export const getMe = () => api.get('/api/auth/me');

export const changePassword = (currentPassword, newPassword) => {
  return api.post('/api/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });
};

// OTP Authentication
export const requestLoginOTP = (email) => {
  return api.post('/api/auth/request-login-otp', { email });
};

export const verifyLoginOTP = (email, otp) => {
  return api.post('/api/auth/verify-login-otp', { email, otp });
};

export const requestPasswordReset = (email) => {
  return api.post('/api/auth/request-password-reset', { email });
};

export const verifyResetOTP = (email, otp) => {
  return api.post('/api/auth/verify-reset-otp', { email, otp });
};

export const resetPassword = (email, otp, newPassword) => {
  return api.post('/api/auth/reset-password', { email, otp, new_password: newPassword });
};

// Admin API
export const getAdminContent = () => api.get('/api/admin/content');

export const updateContent = (section, data) => {
  return api.put('/api/admin/content', { section, data });
};

export const getInquiries = (skip = 0, limit = 50) => {
  return api.get('/api/admin/inquiries', { params: { skip, limit } });
};

export const getAnalytics = (days = 7) => {
  return api.get('/api/admin/analytics', { params: { days } });
};

export const exportAnalyticsCSV = (days = 30) => {
  return api.get('/api/admin/analytics/export/csv', { 
    params: { days },
    responseType: 'blob'
  });
};

export const exportAnalyticsPDF = (days = 30) => {
  return api.get('/api/admin/analytics/export/pdf', { params: { days } });
};

// Heatmap analytics
export const getHeatmapData = (days = 7) => {
  return api.get('/api/admin/heatmap', { params: { days } });
};

// Real-time visitors
export const getRealtimeVisitors = () => {
  return api.get('/api/admin/realtime-visitors');
};

// Session History
export const getSessionHistory = (params = {}) => {
  const { dateRange = 'today', startDate, endDate, page = 1, limit = 25 } = params;
  return api.get('/api/admin/sessions', {
    params: {
      date_range: dateRange,
      start_date: startDate,
      end_date: endDate,
      page,
      limit
    }
  });
};

export const getSettings = () => api.get('/api/admin/settings');

export const updateSettings = (settings) => {
  return api.put('/api/admin/settings', settings);
};

export const generateContent = (prompt, section, tone = 'professional') => {
  return api.post('/api/admin/openai/generate', { prompt, section, tone });
};

export const getOpenAIHistory = (skip = 0, limit = 20) => {
  return api.get('/api/admin/openai/history', { params: { skip, limit } });
};

export const publishGeneration = (generationId, section) => {
  return api.put(`/api/admin/openai/publish/${generationId}`, null, {
    params: { section },
  });
};

export const getAuditLog = (skip = 0, limit = 50) => {
  return api.get('/api/admin/audit-log', { params: { skip, limit } });
};

export const sendTestAnalyticsReport = () => {
  return api.post('/api/admin/send-test-analytics-report');
};

export default api;
